/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable consistent-return */
import notify from "../../utils/notify";
import { authorizedApiClient } from "../config";
import axios from "axios";
import {
  CreateWorkspace,
  CreateWorkspaceResponse,
  GetWorkspace,
  GetWorkspaceResponse,
  WorkspaceObjectRelationshipsQuery,
  WorkspaceObjectRelationshipsResponse,
  UpdateWorkspace,
  UpdateWorkspaceResponse,
  UserPreferenceResponse,
  WorkspaceObjectSearchQuery,
  WorkspaceObjectSearchResponse,
  WorkspaceFieldsAndMetricsQuery,
  WorkspaceFieldsAndMetricsResponse,
  WorkspacePreferenceResponse,
} from "./workspaceEndpoints.interface";

const showErrorAlert = (err: any) => notify({
  heading: "Unknown Error",
  type: "failure",
  messageList: [
    "Kindly contact Hyperengage support to help you resolve this issue.",
  ],
});

export const getWorkspaceFieldsAndMetrics = async ({ workspaceId }: WorkspaceFieldsAndMetricsQuery) => {
  try {
    const response = await authorizedApiClient.get<WorkspaceFieldsAndMetricsResponse>(
      `/workspace/${workspaceId}/fields-and-metrics`,
    );
    return response.data;
  } catch (err) {
    console.log(err);
    showErrorAlert(err);
  }
};

export const getWorkspaceObjectSearchAgainstQuery = async (
  {
    workspaceId, query, signal,
  }: WorkspaceObjectSearchQuery,
) => {
  try {
    const response = await authorizedApiClient.post<WorkspaceObjectSearchResponse>(
      `/workspace/${workspaceId}/object-search`,
      query,
      {
        ...(signal?.token ? {
          cancelToken: signal?.token,
        } : {}),
      },
    );
    return response.data;
  } catch (err: any) {
    if (axios.isCancel(err) || err?.code === "ERR_CANCELED") {
      return;
    }

    if (err?.message === "Component unmounted or effect re-ran") {
      throw err;
    }

    console.error(err);
    showErrorAlert(err);
    return err;
  }
};

export const createWorkspace = async ({
  is_default_workspace, workspace_name, workspace_owner_id, workspace_time_zone, organisation_id,
}: CreateWorkspace) => authorizedApiClient.post<CreateWorkspaceResponse>("/workspace/", {
  workspace_name,
  workspace_owner_id,
  workspace_time_zone,
  organisation_id,
  is_default_workspace,
});

export const getWorkspace = async ({ workspace_id }: GetWorkspace) => authorizedApiClient.get<GetWorkspaceResponse>(`/workspace/${workspace_id}`);

export const updateWorkspace = async ({
  workspace_id,
  tracking_website_domain,
  setup_complete,
  workspace_logo,
  workspace_time_zone,
  workspace_default_currency,
  getting_started_guide_dismissed,
  is_default_workspace,
}: UpdateWorkspace) => authorizedApiClient.put<UpdateWorkspaceResponse>(`/workspace/${workspace_id}`, {
  tracking_website_domain,
  getting_started_guide_dismissed,
  setup_complete,
  workspace_logo,
  workspace_time_zone,
  workspace_default_currency,
  is_default_workspace,
});

export const getDefaultWorkspacePreferences = async () => authorizedApiClient.get<WorkspacePreferenceResponse>("/workspace/preferences/workspace");

export const getDefaultUserPreferences = async () => authorizedApiClient.get<UserPreferenceResponse>("/workspace/preferences/user");

// Data Management endpoints
export const getWorkspaceObjectsDetailed = async ({ workspace_id }: { workspace_id: string }) =>
  authorizedApiClient.get(`/workspace/${workspace_id}/objects`, {
    params: {
      include_disabled: true,
    },
  });

export const getWorkspaceObjectWithFields = async ({
  workspace_id,
  object_id
}: {
  workspace_id: string;
  object_id: string;
}) => authorizedApiClient.get(`/workspace/${workspace_id}/objects/${object_id}`);

export const updateWorkspaceObject = async ({
  workspace_id,
  object_id,
  data
}: {
  workspace_id: string;
  object_id: string;
  data: any
}) => authorizedApiClient.put(`/workspace/${workspace_id}/objects/${object_id}`, data);

export const createWorkspaceObject = async ({
  workspace_id,
  data
}: {
  workspace_id: string;
  data: any
}) => authorizedApiClient.post(`/workspace/${workspace_id}/objects`, data);

export const getWorkspaceObjectRelationships = async ({
  workspace_id,
  include_disabled = true,
  include_disabled_objects = true,
}: WorkspaceObjectRelationshipsQuery) => authorizedApiClient.get<WorkspaceObjectRelationshipsResponse>(
  `/workspace/${workspace_id}/relationships`,
  {
    params: {
      include_disabled,
      include_disabled_objects,
    },
  },
);

export const getWorkspaceFields = async ({
  workspace_id
}: {
  workspace_id: string;
}) => authorizedApiClient.get(`/workspace/${workspace_id}/fields`);

export const getObjectFields = async ({
  workspace_id,
  object_id
}: {
  workspace_id: string;
  object_id: string;
}) => authorizedApiClient.get(`/workspace/${workspace_id}/objects/${object_id}/fields`);

export const createObjectField = async ({
  workspace_id,
  object_id,
  data
}: {
  workspace_id: string;
  object_id: string;
  data: any;
}) => authorizedApiClient.post(`/workspace/${workspace_id}/objects/${object_id}/fields`, data);

export const createBulkObjectFields = async ({
  workspace_id,
  object_id,
  data
}: {
  workspace_id: string;
  object_id: string;
  data: any[];
}) => authorizedApiClient.post(`/workspace/${workspace_id}/objects/${object_id}/fields/bulk`, data);

export const updateWorkspaceField = async ({
  workspace_id,
  field_id,
  data
}: {
  workspace_id: string;
  field_id: string;
  data: any
}) => authorizedApiClient.put(`/workspace/${workspace_id}/fields/${field_id}`, data);

// Note: Metrics configuration endpoints are not available in the current API
// The metrics tab will be implemented when these endpoints become available
